import * as React from "react";

interface AnimatedCuriosidadeCardProps {
	children: React.ReactNode;
	className?: string;
	delay?: number;
}

export function AnimatedCuriosidadeCard({
	children,
	className,
	delay = 0,
}: AnimatedCuriosidadeCardProps) {
	const cardRef = React.useRef<HTMLDivElement>(null);
	const [isVisible, setIsVisible] = React.useState(false);
	const [opacity, setOpacity] = React.useState(0);

	React.useEffect(() => {
		const element = cardRef.current;
		if (!element) return;

		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) {
					setIsVisible(true);
					observer.disconnect();
				}
			},
			{ threshold: 0.1 },
		);

		observer.observe(element);

		return () => observer.disconnect();
	}, []);

	React.useEffect(() => {
		if (!isVisible) return;

		const startTime = performance.now();
		const duration = 1200;

		function animate(currentTime: number) {
			const elapsed = currentTime - startTime - delay;
			if (elapsed < 0) {
				requestAnimationFrame(animate);
				return;
			}

			const progress = Math.min(elapsed / duration, 1);

			const easeOut = (t: number): number => {
				return 1 - Math.pow(1 - t, 2);
			};

			const easedProgress = easeOut(progress);
			const currentOpacity = easedProgress;

			setOpacity(currentOpacity);

			if (progress < 1) {
				requestAnimationFrame(animate);
			} else {
				setOpacity(1);
			}
		}

		requestAnimationFrame(animate);
	}, [isVisible, delay]);

	return (
		<div ref={cardRef} className={className} style={{ opacity }}>
			{children}
		</div>
	);
}
